import React from 'react';
import { motion } from 'framer-motion';
import { Paper, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Notification } from '../types';
import AvatarChip from '../../../components/common/AvatarChip.tsx';

interface FloatingNotificationsProps {
  notification: Notification;
}

const NotificationContainer = styled(motion(Paper))(({ theme }) => ({
  position: 'absolute',
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(1),
  maxWidth: '300px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(138, 43, 226, 0.2)',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(138, 43, 226, 0.3))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.8)',
    transform: 'scale(1.02)',
    boxShadow: '0 0 20px rgba(138, 43, 226, 0.5)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  }
}));

const FloatingNotifications: React.FC<FloatingNotificationsProps> = ({ notification }) => {
  // Determine notification type color
  const getNotificationColor = () => {
    switch (notification.type) {
      case 'info':
        return '#2196F3';
      case 'success':
        return '#00CC99';
      case 'warning':
        return '#FF6F00';
      default:
        return '#8A2BE2';
    }
  };

  return (
    <NotificationContainer
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      style={{
        top: notification.position.y,
        left: notification.position.x,
      }}
    >
      <Box sx={{ mb: 1 }}>
        <AvatarChip
          name={notification.title}
          avatarColor={getNotificationColor()}
          avatarSize="small"
          status={notification.type === 'info' ? 'active' : notification.type === 'warning' ? 'busy' : 'idle'}
        />
      </Box>
      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 1 }}>
        {notification.message}
      </Typography>
    </NotificationContainer>
  );
};

export default FloatingNotifications; 